import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Login } from './components/Login';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './components/Dashboard';
import { ChecklistAgent } from './components/agent/ChecklistAgent';
import { InfoPrincipaliForm } from './components/forms/InfoPrincipaliForm';
import { ParteAForm } from './components/forms/ParteAForm';
import { ParteBForm } from './components/forms/ParteBForm';
import { ParteCForm } from './components/forms/ParteCForm';
import {
  User,
  Bank,
  AuthState,
  ChecklistType,
  ChecklistRecord,
  TipologiaOperazione,
  createEmptyChecklistRecord,
  createEmptyParteA,
  createEmptyParteB,
  createEmptyParteC,
  CHECKLIST_NAMES
} from './types/datamodel';
import { calcolaTuttiEsitiRecord } from './utils/formulasNew';
import { Save, Building2, Search, X } from 'lucide-react';

// Demo company name
const COMPANY_NAME = 'AXA Insurance Company Demo';

// Demo banks
const DEMO_BANKS: Bank[] = [
  { id: 'bank-1', name: 'Banca Distributrice A', code: 'BDA', contactPerson: 'Mario Rossi', createdAt: new Date(), updatedAt: new Date() },
  { id: 'bank-2', name: 'Banca Distributrice B', code: 'BDB', contactPerson: 'Laura Bianchi', createdAt: new Date(), updatedAt: new Date() },
  { id: 'bank-3', name: 'Banca Distributrice C', code: 'BDC', contactPerson: 'Giuseppe Verdi', createdAt: new Date(), updatedAt: new Date() },
  { id: 'bank-4', name: 'Banca Distributrice D', code: 'BDD', contactPerson: 'Anna Neri', createdAt: new Date(), updatedAt: new Date() }
];

// Bank selector for Company users
function BankSelector({
  currentBank,
  viewAllBanks,
  onBankChange,
  allBanks
}: {
  currentBank: Bank | null;
  viewAllBanks: boolean;
  onBankChange: (bankId: string) => void;
  allBanks: Bank[];
}) {
  return (
    <div className="flex items-center space-x-3 bg-white px-4 py-3 border-b border-gray-200">
      <Building2 className="w-5 h-5 text-indigo-600" />
      <label className="text-sm font-medium text-gray-700">Filtra per Banca:</label>
      <select
        value={viewAllBanks ? 'all' : currentBank?.id || ''}
        onChange={(e) => onBankChange(e.target.value)}
        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm font-medium"
      >
        <option value="all">Tutte le Banche</option>
        {allBanks.map(bank => (
          <option key={bank.id} value={bank.id}>{bank.name}</option>
        ))}
      </select>
      {viewAllBanks && (
        <span className="text-sm text-gray-500 italic">
          Vista aggregata di tutte le banche
        </span>
      )}
      {!viewAllBanks && currentBank && (
        <span className="text-sm text-gray-500">
          Visualizzazione: <strong>{currentBank.name}</strong>
        </span>
      )}
    </div>
  );
}

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Aggregated view for all banks
function AllBanksChecklistView({
  type,
  records,
  allBanks,
  loading
}: {
  type: ChecklistType;
  records: any[];  // Array from database
  allBanks: Bank[];
  loading?: boolean;
}) {
  const [searchNumeroPolizza, setSearchNumeroPolizza] = React.useState('');
  const [searchCodiceFiscale, setSearchCodiceFiscale] = React.useState('');
  const [searchNominativo, setSearchNominativo] = React.useState('');
  const [selectedRecord, setSelectedRecord] = React.useState<{ bank: Bank; record: ChecklistRecord } | null>(null);

  // Convert database records to display format
  console.log('🔄 AllBanksChecklistView - Converting records:', records.length);
  const allRecords = records.map(dbRecord => {
    const bank = allBanks.find(b => b.id === dbRecord.bank_id);
    return {
      bank: bank || { id: dbRecord.bank_id, name: dbRecord.bank_name || 'Sconosciuta', code: '', contactPerson: '', createdAt: new Date(), updatedAt: new Date() },
      record: {
        id: dbRecord.id?.toString() || '',
        bankId: dbRecord.bank_id,
        checklistType: dbRecord.checklist_type,
        createdAt: new Date(dbRecord.created_at),
        updatedAt: new Date(dbRecord.updated_at),
        createdBy: dbRecord.created_by,
        infoPrincipali: {
          numeroPolizza: dbRecord.numero_polizza || '',
          compagnia: dbRecord.compagnia || '',
          tipologiaProdotto: '',
          nomeCommerciale: '',
          codiceFiscaleContraente: dbRecord.codice_fiscale_contraente || '',
          nominativoContraente: dbRecord.nominativo_contraente || '',
          tipologiaOperazione: '' as TipologiaOperazione,
          dataSottoscrizione: dbRecord.data_decorrenza ? new Date(dbRecord.data_decorrenza) : null,
          dataOperazione: dbRecord.data_scadenza ? new Date(dbRecord.data_scadenza) : null,
          importo: null,
          dataControllo: null,
          controlloEffettuato: '',
          referente: ''
        },
        parteA: dbRecord.parte_a || {},
        parteB: dbRecord.parte_b || {},
        parteC: dbRecord.parte_c || {},
        status: 'completed',
        notes: ''
      } as ChecklistRecord
    };
  });

  // Filtra i record in base ai criteri di ricerca
  const filteredRecords = allRecords.filter(({ record }) => {
    const matchNumero = !searchNumeroPolizza ||
      record.infoPrincipali.numeroPolizza.toLowerCase().includes(searchNumeroPolizza.toLowerCase());
    const matchCF = !searchCodiceFiscale ||
      record.infoPrincipali.codiceFiscaleContraente.toLowerCase().includes(searchCodiceFiscale.toLowerCase());
    const matchNominativo = !searchNominativo ||
      record.infoPrincipali.nominativoContraente.toLowerCase().includes(searchNominativo.toLowerCase());

    return matchNumero && matchCF && matchNominativo;
  });

  console.log('📊 AllBanksChecklistView - Filtered:', {
    total: allRecords.length,
    filtered: filteredRecords.length,
    filters: { searchNumeroPolizza, searchCodiceFiscale, searchNominativo }
  });

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">{CHECKLIST_NAMES[type]} - Tutte le Banche</h1>

      {/* Filtri di ricerca */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
        <div className="flex items-center mb-4">
          <Search className="w-5 h-5 text-indigo-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-800">Filtri di Ricerca</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Numero Polizza
            </label>
            <input
              type="text"
              value={searchNumeroPolizza}
              onChange={(e) => setSearchNumeroPolizza(e.target.value)}
              placeholder="Cerca per numero polizza..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Codice Fiscale
            </label>
            <input
              type="text"
              value={searchCodiceFiscale}
              onChange={(e) => setSearchCodiceFiscale(e.target.value)}
              placeholder="Cerca per codice fiscale..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nominativo Contraente
            </label>
            <input
              type="text"
              value={searchNominativo}
              onChange={(e) => setSearchNominativo(e.target.value)}
              placeholder="Cerca per nominativo..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        {(searchNumeroPolizza || searchCodiceFiscale || searchNominativo) && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Trovati <strong>{filteredRecords.length}</strong> record su {allRecords.length}
            </p>
            <button
              onClick={() => {
                setSearchNumeroPolizza('');
                setSearchCodiceFiscale('');
                setSearchNominativo('');
              }}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Cancella filtri
            </button>
          </div>
        )}
      </div>

      {/* Lista record */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento record...</p>
        </div>
      ) : filteredRecords.length > 0 ? (
        <div className="grid grid-cols-1 gap-6">
          {filteredRecords.map(({ bank, record }) => (
            <div key={`${bank.id}-${record.id}`} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-800">{bank.name}</h3>
                <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
                  {bank.code}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Stato</p>
                  <p className="text-lg font-semibold capitalize">
                    {record.status === 'draft' && '📝 Bozza'}
                    {record.status === 'completed' && '✅ Completato'}
                    {record.status === 'reviewed' && '👁️ Revisionato'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">N° Polizza</p>
                  <p className="text-lg font-semibold">{record.infoPrincipali.numeroPolizza || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Codice Fiscale</p>
                  <p className="text-sm font-semibold">{record.infoPrincipali.codiceFiscaleContraente || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Contraente</p>
                  <p className="text-lg font-semibold">{record.infoPrincipali.nominativoContraente || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Ultimo Aggiornamento</p>
                  <p className="text-sm text-gray-700">
                    {new Date(record.updatedAt).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setSelectedRecord({ bank, record })}
                className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                Visualizza Dettagli
              </button>
            </div>
          ))}
        </div>
      ) : allRecords.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-800 font-medium">
            Nessun controllo presente per questa checklist in nessuna banca distributrice.
          </p>
          <p className="text-yellow-700 text-sm mt-2">
            Seleziona una banca specifica dal menu sopra per iniziare a compilare i controlli.
          </p>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-600 font-medium">
            Nessun record corrisponde ai filtri di ricerca.
          </p>
          <button
            onClick={() => {
              setSearchNumeroPolizza('');
              setSearchCodiceFiscale('');
              setSearchNominativo('');
            }}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Cancella filtri
          </button>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 <strong>Suggerimento:</strong> Seleziona una banca specifica dal menu sopra per visualizzare e modificare i dettagli completi.
        </p>
      </div>

      {/* Modal per visualizzare dettagli record */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Dettagli Record</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Banca: {selectedRecord.bank.name} • Polizza: {selectedRecord.record.infoPrincipali.numeroPolizza}
                </p>
              </div>
              <button
                onClick={() => setSelectedRecord(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <ChecklistPage
                type={type}
                currentRecord={selectedRecord.record}
                onSave={() => {}}
                currentBank={selectedRecord.bank}
              />
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => setSelectedRecord(null)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Checklist page with tabs
function ChecklistPage({
  type,
  currentRecord,
  onSave,
  currentBank
}: {
  type: ChecklistType;
  currentRecord: ChecklistRecord | null;
  onSave: (record: ChecklistRecord) => void;
  currentBank: Bank | null;
}) {
  const [activeTab, setActiveTab] = useState<'info' | 'parteA' | 'parteB' | 'parteC'>('info');
  const [record, setRecord] = useState<ChecklistRecord | null>(currentRecord);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Reset record when type or bank changes, or when currentRecord changes
    if (currentBank) {
      if (currentRecord) {
        // Use existing record from parent
        setRecord(currentRecord);
      } else {
        // Create new empty record - use a valid user ID from the database
        const newRecord = createEmptyChecklistRecord(currentBank.id, type, 'user-company');
        setRecord(newRecord);
      }
      setHasChanges(false);
    }
  }, [currentBank, type, currentRecord]);

  const handleInfoChange = (newInfo: ChecklistRecord['infoPrincipali']) => {
    if (!record) return;
    const updated = { ...record, infoPrincipali: newInfo };
    setRecord(updated);
    setHasChanges(true);
  };

  const handleParteAChange = (newParteA: ChecklistRecord['parteA']) => {
    if (!record) return;
    let updated = { ...record, parteA: newParteA };
    // Ricalcola automaticamente gli esiti
    updated = calcolaTuttiEsitiRecord(updated, type);
    setRecord(updated);
    setHasChanges(true);
  };

  const handleParteBChange = (newParteB: ChecklistRecord['parteB']) => {
    if (!record) return;
    let updated = { ...record, parteB: newParteB };
    // Ricalcola automaticamente gli esiti
    updated = calcolaTuttiEsitiRecord(updated, type);
    setRecord(updated);
    setHasChanges(true);
  };

  const handleParteCChange = (newParteC: ChecklistRecord['parteC']) => {
    if (!record) return;
    let updated = { ...record, parteC: newParteC };
    // Ricalcola automaticamente gli esiti
    updated = calcolaTuttiEsitiRecord(updated, type);
    setRecord(updated);
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!record) return;
    onSave(record);
    setHasChanges(false);
  };

  if (!record) {
    return <div className="p-8">Caricamento...</div>;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">{CHECKLIST_NAMES[type]}</h1>
          <p className="text-gray-600 mt-1">Gestione controlli per {CHECKLIST_NAMES[type]}</p>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
            hasChanges
              ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <Save className="w-5 h-5" />
          <span>{hasChanges ? 'Salva Modifiche' : 'Salvato'}</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white">
        <div className="flex space-x-1 px-8">
          {[
            { id: 'info' as const, label: 'Info Principali' },
            { id: 'parteA' as const, label: 'Parte A - Clienti' },
            { id: 'parteB' as const, label: 'Parte B - AML' },
            { id: 'parteC' as const, label: 'Parte C - Altri' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {activeTab === 'info' && (
          <InfoPrincipaliForm
            data={record.infoPrincipali}
            checklistType={type}
            onChange={handleInfoChange}
          />
        )}

        {activeTab === 'parteA' && (
          <ParteAForm
            data={record.parteA}
            infoPrincipali={record.infoPrincipali}
            checklistType={type}
            onChange={handleParteAChange}
          />
        )}

        {activeTab === 'parteB' && (
          <ParteBForm
            data={record.parteB}
            onChange={handleParteBChange}
          />
        )}

        {activeTab === 'parteC' && (
          <ParteCForm
            data={record.parteC}
            checklistType={type}
            onChange={handleParteCChange}
          />
        )}
      </div>
    </div>
  );
}

// Company overview
function CompanyOverview({ allBanks, currentBank, viewAllBanks }: { allBanks: Bank[]; currentBank: Bank | null; viewAllBanks: boolean }) {
  const [bankStats, setBankStats] = useState<Record<string, { total: number; byType: Record<string, number> }>>({});
  const [loading, setLoading] = useState(true);

  // Determina quali banche mostrare in base al filtro
  const banksToShow = viewAllBanks ? allBanks : (currentBank ? [currentBank] : allBanks);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      const stats: Record<string, { total: number; byType: Record<string, number> }> = {};

      // Initialize stats for banks to show
      banksToShow.forEach(bank => {
        stats[bank.id] = { total: 0, byType: {} };
      });

      // Load records for each checklist type
      const types: ChecklistType[] = ['ibip', 'protection', 'motor', 'pnc', 'ppi'];

      try {
        for (const type of types) {
          // Se è selezionata una banca specifica, carica solo i suoi record
          const endpoint = viewAllBanks || !currentBank
            ? `${API_BASE_URL}/api/checklist/all/${type}`
            : `${API_BASE_URL}/api/checklist/${currentBank.id}/${type}`;

          const response = await fetch(endpoint);
          if (response.ok) {
            const data = await response.json();
            const records = data.records || [];

            // Count records per bank
            records.forEach((record: any) => {
              const bankId = record.bank_id;
              if (stats[bankId]) {
                stats[bankId].total += 1;
                stats[bankId].byType[type] = (stats[bankId].byType[type] || 0) + 1;
              }
            });
          }
        }
        setBankStats(stats);
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [allBanks, currentBank, viewAllBanks]);

  const titleText = viewAllBanks ? 'Vista Compagnia - Tutte le Banche' : `Vista Compagnia - ${currentBank?.name || 'Banca'}`;
  const subtitleText = viewAllBanks
    ? 'Panoramica olistica di tutti i controlli per tutte le banche distributrici'
    : `Controlli per ${currentBank?.name || 'la banca selezionata'}`;

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">{titleText}</h1>
        <p className="text-lg text-indigo-600 font-semibold mb-4">{COMPANY_NAME}</p>
      </div>
      <p className="text-gray-600 mb-8">{subtitleText}</p>

      {loading ? (
        <div className="text-center py-8">
          <p className="text-gray-500">Caricamento statistiche...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {banksToShow.map(bank => {
            const stats = bankStats[bank.id] || { total: 0, byType: {} };
            return (
              <div key={bank.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{bank.name}</h3>
                <p className="text-sm text-gray-600 mb-4">Codice: {bank.code}</p>
                <div className="grid grid-cols-6 gap-4">
                  <div className="text-center p-3 bg-indigo-50 rounded">
                    <p className="text-2xl font-bold text-indigo-600">{stats.total}</p>
                    <p className="text-xs text-gray-600">Totale Controlli</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded">
                    <p className="text-2xl font-bold text-blue-600">{stats.byType['ibip'] || 0}</p>
                    <p className="text-xs text-gray-600">IBIP</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded">
                    <p className="text-2xl font-bold text-green-600">{stats.byType['protection'] || 0}</p>
                    <p className="text-xs text-gray-600">Protection</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded">
                    <p className="text-2xl font-bold text-yellow-600">{stats.byType['motor'] || 0}</p>
                    <p className="text-xs text-gray-600">Motor</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded">
                    <p className="text-2xl font-bold text-purple-600">{stats.byType['pnc'] || 0}</p>
                    <p className="text-xs text-gray-600">P&C</p>
                  </div>
                  <div className="text-center p-3 bg-pink-50 rounded">
                    <p className="text-2xl font-bold text-pink-600">{stats.byType['ppi'] || 0}</p>
                    <p className="text-xs text-gray-600">PPI</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Wrapper for checklist routes
function ChecklistRouteWrapper({
  type,
  records,
  onSave,
  onDelete,
  currentBank,
  viewAllBanks,
  allBanks
}: {
  type: ChecklistType;
  records: Map<string, ChecklistRecord>;
  onSave: (record: ChecklistRecord) => void;
  onDelete: (recordId: string, bankId: string, checklistType: ChecklistType) => void;
  currentBank: Bank | null;
  viewAllBanks: boolean;
  allBanks: Bank[];
}) {
  const [savedRecords, setSavedRecords] = React.useState<any[]>([]);
  const [selectedRecordId, setSelectedRecordId] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Load saved records from database
  React.useEffect(() => {
    const loadRecords = async () => {
      console.log('🔄 ChecklistRouteWrapper loadRecords:', {
        type,
        viewAllBanks,
        currentBankId: currentBank?.id,
        currentBankName: currentBank?.name
      });

      setLoading(true);
      try {
        let endpoint: string;

        if (viewAllBanks) {
          // Carica tutti i record di tutte le banche
          endpoint = `${API_BASE_URL}/api/checklist/all/${type}`;
          console.log('📡 Using /all/ endpoint:', endpoint);
        } else if (currentBank) {
          // Carica solo i record della banca selezionata
          endpoint = `${API_BASE_URL}/api/checklist/${currentBank.id}/${type}`;
          console.log('📡 Using bank-specific endpoint:', endpoint);
        } else {
          // Nessuna banca selezionata
          console.log('⚠️  No bank selected and viewAllBanks=false, skipping load');
          setLoading(false);
          return;
        }

        console.log('🌐 Fetching:', endpoint);
        const response = await fetch(endpoint);
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Loaded', data.records?.length || 0, 'records for', type);
          console.log('📦 First 3 records:', data.records?.slice(0, 3).map((r: any) => ({
            id: r.id,
            numero_polizza: r.numero_polizza,
            bank_id: r.bank_id,
            bank_name: r.bank_name
          })));
          setSavedRecords(data.records || []);
        } else {
          console.error('❌ Response not OK:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('❌ Error loading records:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRecords();
  }, [currentBank, type, viewAllBanks]);

  // Se è selezionato "Tutte le Banche", mostra la vista aggregata
  if (viewAllBanks) {
    return (
      <AllBanksChecklistView
        type={type}
        records={savedRecords}
        allBanks={allBanks}
        loading={loading}
      />
    );
  }

  // Get current record to edit (either from local state or selected from list)
  const key = currentBank ? `${currentBank.id}-${type}` : type;
  let currentRecord = records.get(key) || null;

  // If a record is selected from the list, use it
  if (selectedRecordId && savedRecords.length > 0) {
    const selectedFromDB = savedRecords.find(r => r.id === selectedRecordId);
    if (selectedFromDB) {
      // Convert from snake_case API format to camelCase
      // If we have info_principali from JSON, use it and convert date strings to Date objects
      let infoPrincipali = selectedFromDB.info_principali || {
        numeroPolizza: selectedFromDB.numero_polizza || '',
        compagnia: selectedFromDB.compagnia || '',
        tipologiaProdotto: '',
        nomeCommerciale: '',
        codiceFiscaleContraente: selectedFromDB.codice_fiscale_contraente || '',
        nominativoContraente: selectedFromDB.nominativo_contraente || '',
        tipologiaOperazione: '',
        dataSottoscrizione: selectedFromDB.data_decorrenza ? new Date(selectedFromDB.data_decorrenza) : null,
        dataOperazione: selectedFromDB.data_scadenza ? new Date(selectedFromDB.data_scadenza) : null,
        importo: null,
        dataControllo: null,
        controlloEffettuato: '',
        referente: ''
      };

      // Convert date strings to Date objects if they exist
      if (infoPrincipali.dataSottoscrizione && typeof infoPrincipali.dataSottoscrizione === 'string') {
        infoPrincipali.dataSottoscrizione = new Date(infoPrincipali.dataSottoscrizione);
      }
      if (infoPrincipali.dataOperazione && typeof infoPrincipali.dataOperazione === 'string') {
        infoPrincipali.dataOperazione = new Date(infoPrincipali.dataOperazione);
      }
      if (infoPrincipali.dataControllo && typeof infoPrincipali.dataControllo === 'string') {
        infoPrincipali.dataControllo = new Date(infoPrincipali.dataControllo);
      }

      currentRecord = {
        id: selectedFromDB.id.toString(),
        bankId: selectedFromDB.bank_id,
        checklistType: selectedFromDB.checklist_type,
        createdAt: new Date(selectedFromDB.created_at),
        updatedAt: new Date(selectedFromDB.updated_at),
        createdBy: selectedFromDB.created_by,
        infoPrincipali: infoPrincipali,
        parteA: selectedFromDB.parte_a || createEmptyParteA(type),
        parteB: selectedFromDB.parte_b || createEmptyParteB(),
        parteC: selectedFromDB.parte_c || createEmptyParteC(type),
        status: 'draft',
        notes: ''
      };
    }
  }

  return (
    <div className="h-full flex">
      {/* Sidebar con lista record salvati */}
      <div className="w-80 border-r border-gray-200 bg-gray-50 overflow-y-auto">
        <div className="p-4 border-b border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800">Record Salvati</h2>
          <p className="text-sm text-gray-600 mt-1">{CHECKLIST_NAMES[type]}</p>
        </div>

        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="text-sm text-gray-600 mt-2">Caricamento...</p>
          </div>
        ) : savedRecords.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <p className="text-sm">Nessun record salvato</p>
            <p className="text-xs mt-1">Crea un nuovo record</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {savedRecords.map((record) => (
              <div
                key={record.id}
                className={`p-4 cursor-pointer hover:bg-white transition-colors ${
                  selectedRecordId === record.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''
                }`}
                onClick={() => setSelectedRecordId(record.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {record.numero_polizza || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 truncate">
                      {record.nominativo_contraente || 'Nessun contraente'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(record.created_at).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Sei sicuro di voler eliminare questo record?')) {
                        onDelete(record.id.toString(), currentBank!.id, type);
                        setSavedRecords(prev => prev.filter(r => r.id !== record.id));
                        if (selectedRecordId === record.id) {
                          setSelectedRecordId(null);
                        }
                      }
                    }}
                    className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded"
                    title="Elimina"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => {
              setSelectedRecordId(null);
            }}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
          >
            + Nuovo Record
          </button>
        </div>
      </div>

      {/* Form area */}
      <div className="flex-1 overflow-hidden">
        <ChecklistPage
          key={`${type}-${selectedRecordId || 'new'}`}
          type={type}
          currentRecord={currentRecord}
          onSave={(record) => {
            onSave(record);
            // Reload records after save
            setTimeout(async () => {
              try {
                const response = await fetch(`${API_BASE_URL}/api/checklist/${currentBank!.id}/${type}`);
                if (response.ok) {
                  const data = await response.json();
                  setSavedRecords(data.records || []);
                }
              } catch (error) {
                console.error('Error reloading records:', error);
              }
            }, 500);
          }}
          currentBank={currentBank}
        />
      </div>
    </div>
  );
}

// Main App
function AppNew() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    selectedBankId: null
  });
  const [currentBank, setCurrentBank] = useState<Bank | null>(null);
  const [records, setRecords] = useState<Map<string, ChecklistRecord>>(new Map());
  const [viewAllBanks, setViewAllBanks] = useState(false);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [banksLoading, setBanksLoading] = useState(true);

  // Load banks from backend on mount
  useEffect(() => {
    const loadBanks = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/banks`);
        if (!response.ok) throw new Error('Failed to load banks');
        const data = await response.json();
        const loadedBanks: Bank[] = data.banks.map((b: any) => ({
          id: b.id,
          name: b.name,
          code: b.code,
          contactPerson: '',
          createdAt: new Date(b.created_at),
          updatedAt: new Date(b.updated_at)
        }));
        setBanks(loadedBanks);
        console.log('✅ Loaded banks from backend:', loadedBanks);
      } catch (error) {
        console.error('❌ Error loading banks:', error);
        // Fallback to demo banks if API fails
        setBanks(DEMO_BANKS);
      } finally {
        setBanksLoading(false);
      }
    };
    loadBanks();
  }, []);

  const handleLogin = (user: User, bank?: Bank) => {
    setAuthState({
      isAuthenticated: true,
      user,
      selectedBankId: bank?.id || null
    });
    if (bank) {
      setCurrentBank(bank);
      setViewAllBanks(false);  // Banca specifica selezionata
    } else {
      // Nessuna banca selezionata = visualizza tutte le banche
      setViewAllBanks(true);
      setCurrentBank(banks[0] || null); // Manteniamo una banca di default per i form
    }
  };

  const handleLogout = () => {
    setAuthState({
      isAuthenticated: false,
      user: null,
      selectedBankId: null
    });
    setCurrentBank(null);
    setRecords(new Map());
  };

  const handleBankChange = (bankId: string) => {
    if (bankId === 'all') {
      setViewAllBanks(true);
      setCurrentBank(banks[0] || null); // Manteniamo una banca di default per i form
    } else {
      setViewAllBanks(false);
      const bank = banks.find(b => b.id === bankId);
      if (bank) {
        setCurrentBank(bank);
      }
    }
  };

  const handleSaveRecord = async (record: ChecklistRecord) => {
    try {
      // Transform record to snake_case format expected by API
      const recordData = {
        bank_id: record.bankId,
        created_by: record.createdBy,
        checklist_type: record.checklistType,
        numero_polizza: record.infoPrincipali.numeroPolizza,
        compagnia: record.infoPrincipali.compagnia || 'Compagnia A',
        data_decorrenza: record.infoPrincipali.dataSottoscrizione?.toISOString() || new Date().toISOString(),
        data_scadenza: record.infoPrincipali.dataOperazione?.toISOString() || new Date().toISOString(),
        nominativo_contraente: record.infoPrincipali.nominativoContraente,
        codice_fiscale_contraente: record.infoPrincipali.codiceFiscaleContraente,
        status: record.status || 'draft'
      };

      console.log('Saving record to API:', recordData);

      // Call API to save record
      const response = await fetch(`${API_BASE_URL}/api/checklist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          record: recordData,
          infoPrincipali: record.infoPrincipali,
          parteA: record.parteA,
          parteB: record.parteB,
          parteC: record.parteC
        }),
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Save successful:', result);

      // Update local state with saved record
      setRecords(prev => {
        const newRecords = new Map(prev);
        const key = `${record.bankId}-${record.checklistType}`;
        newRecords.set(key, record);
        return newRecords;
      });

      alert('✅ Record salvato con successo nel database!');
    } catch (error) {
      console.error('Error saving record:', error);
      alert(`❌ Errore durante il salvataggio: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteRecord = async (recordId: string, bankId: string, checklistType: ChecklistType) => {
    try {
      console.log('Deleting record:', recordId);

      const response = await fetch(`${API_BASE_URL}/api/checklist/${recordId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Remove from local state
      setRecords(prev => {
        const newRecords = new Map(prev);
        const key = `${bankId}-${checklistType}`;
        newRecords.delete(key);
        return newRecords;
      });

      alert('✅ Record eliminato con successo!');
      console.log('Delete successful');
    } catch (error) {
      console.error('Error deleting record:', error);
      alert(`❌ Errore durante l'eliminazione: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (!authState.isAuthenticated || !authState.user) {
    if (banksLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Caricamento banche...</p>
          </div>
        </div>
      );
    }
    return <Login onLogin={handleLogin} availableBanks={banks} />;
  }

  return (
    <BrowserRouter>
      <div className="flex h-screen bg-gray-100">
        <Sidebar user={authState.user} currentBank={currentBank} viewAllBanks={viewAllBanks} onLogout={handleLogout} />

        <div className="flex-1 overflow-auto flex flex-col">
          {/* Bank selector for company users */}
          {authState.user?.role === 'company' && (
            <BankSelector
              currentBank={currentBank}
              viewAllBanks={viewAllBanks}
              onBankChange={handleBankChange}
              allBanks={banks}
            />
          )}

          <div className="flex-1 overflow-auto">
            <Routes>
              <Route
                path="/"
                element={(() => {
                  // Per utenti bank: usa sempre la loro banca
                  // Per utenti company: usa la banca selezionata (o undefined se viewAllBanks)
                  const computedBankId = viewAllBanks ? undefined : currentBank?.id;
                  console.log('🚀 AppNew Passing to Dashboard:', {
                    userRole: authState.user?.role,
                    viewAllBanks,
                    currentBankId: currentBank?.id,
                    computedBankId
                  });
                  return (
                    <Dashboard
                      userRole={authState.user?.role || 'bank'}
                      currentBankId={computedBankId}
                      apiBaseUrl={API_BASE_URL}
                    />
                  );
                })()}
              />

              <Route
                path="/agent"
                element={
                  <ChecklistAgent
                    user={authState.user}
                    currentBank={currentBank || undefined}
                  />
                }
              />

            <Route
              path="/checklist/ibip"
              element={
                <ChecklistRouteWrapper
                  type="ibip"
                  records={records}
                  onSave={handleSaveRecord}
                  onDelete={handleDeleteRecord}
                  currentBank={currentBank}
                  viewAllBanks={viewAllBanks}
                  allBanks={banks}
                />
              }
            />
            <Route
              path="/checklist/protection"
              element={
                <ChecklistRouteWrapper
                  type="protection"
                  records={records}
                  onSave={handleSaveRecord}
                  onDelete={handleDeleteRecord}
                  currentBank={currentBank}
                  viewAllBanks={viewAllBanks}
                  allBanks={banks}
                />
              }
            />
            <Route
              path="/checklist/motor"
              element={
                <ChecklistRouteWrapper
                  type="motor"
                  records={records}
                  onSave={handleSaveRecord}
                  onDelete={handleDeleteRecord}
                  currentBank={currentBank}
                  viewAllBanks={viewAllBanks}
                  allBanks={banks}
                />
              }
            />
            <Route
              path="/checklist/pnc"
              element={
                <ChecklistRouteWrapper
                  type="pnc"
                  records={records}
                  onSave={handleSaveRecord}
                  onDelete={handleDeleteRecord}
                  currentBank={currentBank}
                  viewAllBanks={viewAllBanks}
                  allBanks={banks}
                />
              }
            />
            <Route
              path="/checklist/ppi"
              element={
                <ChecklistRouteWrapper
                  type="ppi"
                  records={records}
                  onSave={handleSaveRecord}
                  onDelete={handleDeleteRecord}
                  currentBank={currentBank}
                  viewAllBanks={viewAllBanks}
                  allBanks={banks}
                />
              }
            />

            {authState.user.role === 'company' && (
              <Route path="/company-overview" element={<CompanyOverview allBanks={banks} currentBank={currentBank} viewAllBanks={viewAllBanks} />} />
            )}

            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </BrowserRouter>
  );
}

export default AppNew;
